import * as React from 'react'
import { cn } from '../../lib/utils'

/**
 * CinqoLoader — motion identity for Cinqo Ops
 *
 * Minimalist waste-logistics loader: stylized top-down territory (districts,
 * road grid) with collection points converging onto 2 mutualization hubs,
 * then flowing to a single recycling facility.
 *
 * Design handoff source : claude.ai/design "Waste Motion" bundle (2026-04-19)
 * — palette strictly from Cinqo tokens (violet #3D2ECC / mint #20B088 /
 *   off-white #FCF8FF), SVG so it scales crisply at any size, 4.8s loop.
 *
 * Usage :
 *   <CinqoLoader />                // 200px default
 *   <CinqoLoader size={120} />     // smaller (context cards)
 *   <CinqoLoader size={64} />      // tight spaces
 *
 * Respects prefers-reduced-motion : routes + particles are frozen at their
 * drawn-in state, hubs show a single static ring (no ripple loop).
 *
 * A11y : role="img" + aria-label by default. Pass your own via `aria-label`.
 */

export interface CinqoLoaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Edge length in px (width = height). Defaut 200. */
  size?: number
  /** Override aria-label (defaut : "Chargement en cours"). */
  'aria-label'?: string
}

// Geometry in a 200-unit coordinate system (scaled by CSS via viewBox)
const H1 = { x: 72, y: 118 } // left mutualization hub
const H2 = { x: 138, y: 86 } // right mutualization hub
const F = { x: 104, y: 150 } // facility / recycler

const pointsA = [
  { x: 36, y: 72 },
  { x: 52, y: 46 },
  { x: 94, y: 56 },
  { x: 32, y: 132 },
  { x: 58, y: 158 },
] as const

const pointsB = [
  { x: 170, y: 48 },
  { x: 158, y: 122 },
  { x: 118, y: 36 },
  { x: 176, y: 96 },
  { x: 142, y: 160 },
] as const

// Quadratic curve with perpendicular bend — routes feel like roads, not lasers.
function curve(
  a: { x: number; y: number },
  b: { x: number; y: number },
  bend = 0.22,
): string {
  const mx = (a.x + b.x) / 2
  const my = (a.y + b.y) / 2
  const dx = b.x - a.x
  const dy = b.y - a.y
  const cx = mx + -dy * bend
  const cy = my + dx * bend
  return `M${a.x},${a.y} Q${cx},${cy} ${b.x},${b.y}`
}

interface Route {
  id: string
  d: string
  delay: number
  weight: number
  color: 'violet' | 'mint' | 'violet-soft'
}

const ROUTES: Route[] = [
  ...pointsA.map((p, i) => ({
    id: `a${i}`,
    d: curve(p, H1, 0.18 + (i % 2) * 0.08),
    delay: i * 0.18,
    weight: 1.2,
    color: 'violet' as const,
  })),
  ...pointsB.map((p, i) => ({
    id: `b${i}`,
    d: curve(p, H2, -0.2 + (i % 2) * 0.1),
    delay: 0.4 + i * 0.18,
    weight: 1.2,
    color: 'violet' as const,
  })),
  // trunk lines — thicker, mint (aggregated volume moving to recycling)
  { id: 't1', d: curve(H1, F, -0.12), delay: 1.6, weight: 2.2, color: 'mint' },
  { id: 't2', d: curve(H2, F, 0.12), delay: 1.8, weight: 2.2, color: 'mint' },
  { id: 't3', d: curve(H1, H2, -0.08), delay: 2.0, weight: 1.6, color: 'violet-soft' },
]

// Stable SVG id suffix — avoid collision when multiple loaders on a page
let _instanceCounter = 0
function useInstanceId(): string {
  const ref = React.useRef<string | null>(null)
  if (ref.current === null) {
    _instanceCounter = (_instanceCounter + 1) % 1_000_000
    ref.current = `cinqo-loader-${_instanceCounter}`
  }
  return ref.current
}

export const CinqoLoader = React.forwardRef<HTMLDivElement, CinqoLoaderProps>(
  ({ size = 200, className, 'aria-label': ariaLabel = 'Chargement en cours', style, ...props }, ref) => {
    const iid = useInstanceId()
    const bgGrad = `${iid}-bg`
    const trunkGrad = `${iid}-trunk`
    const districtA = `${iid}-distA`
    const districtB = `${iid}-distB`
    const districtC = `${iid}-distC`
    const mapClip = `${iid}-clip`

    return (
      <div
        ref={ref}
        role="img"
        aria-label={ariaLabel}
        className={cn('cinqo-loader relative inline-block', className)}
        style={{ width: size, height: size, ...style }}
        {...props}
      >
        <svg
          viewBox="0 0 200 200"
          width={size}
          height={size}
          style={{ display: 'block' }}
          aria-hidden="true"
        >
          <defs>
            {/* soft radial vignette on background */}
            <radialGradient id={bgGrad} cx="50%" cy="45%" r="65%">
              <stop offset="0%" stopColor="#FFFFFF" />
              <stop offset="70%" stopColor="#FAF7FE" />
              <stop offset="100%" stopColor="#F2EEFB" />
            </radialGradient>

            {/* mint gradient for trunk lines — fades to violet at facility */}
            <linearGradient id={trunkGrad} x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#45D4B1" />
              <stop offset="100%" stopColor="#3D2ECC" />
            </linearGradient>

            {/* district fills — barely-there tonal variation */}
            <linearGradient id={districtA} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#F5F2FE" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#EFECF8" stopOpacity="0.5" />
            </linearGradient>
            <linearGradient id={districtB} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#F0F1F9" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#E8E9F0" stopOpacity="0.35" />
            </linearGradient>
            <linearGradient id={districtC} x1="0" y1="1" x2="1" y2="0">
              <stop offset="0%" stopColor="#E5FAF5" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#F5F2FE" stopOpacity="0.35" />
            </linearGradient>

            <clipPath id={mapClip}>
              <rect x="0" y="0" width="200" height="200" rx="16" ry="16" />
            </clipPath>
          </defs>

          {/* ── Background ─────────────────────────────────────────── */}
          <g clipPath={`url(#${mapClip})`}>
            <rect x="0" y="0" width="200" height="200" fill={`url(#${bgGrad})`} />

            {/* District polygons — abstract urban zones */}
            <path d="M 0,0 L 88,0 L 104,38 L 72,82 L 0,64 Z" fill={`url(#${districtA})`} />
            <path d="M 88,0 L 200,0 L 200,72 L 128,60 L 104,38 Z" fill={`url(#${districtB})`} />
            <path d="M 0,64 L 72,82 L 88,140 L 48,200 L 0,200 Z" fill={`url(#${districtC})`} />
            <path
              d="M 200,72 L 200,200 L 48,200 L 88,140 L 72,82 L 128,60 Z"
              fill="#F7F5FC"
              opacity="0.6"
            />

            {/* Road grid — thin horizontal + vertical lines, barely visible */}
            <g stroke="#E8E9F0" strokeWidth="0.6" opacity="0.75">
              <line x1="0" y1="38" x2="200" y2="38" />
              <line x1="0" y1="82" x2="200" y2="82" />
              <line x1="0" y1="126" x2="200" y2="126" />
              <line x1="0" y1="170" x2="200" y2="170" />
              <line x1="40" y1="0" x2="40" y2="200" />
              <line x1="88" y1="0" x2="88" y2="200" />
              <line x1="138" y1="0" x2="138" y2="200" />
              <line x1="178" y1="0" x2="178" y2="200" />
            </g>

            {/* Diagonal minor roads — subtle, add urban feel */}
            <g stroke="#EFECF8" strokeWidth="0.5" opacity="0.8">
              <line x1="0" y1="200" x2="200" y2="40" />
              <line x1="200" y1="200" x2="40" y2="0" />
            </g>

            {/* ── Routes (drawn + flowing) ────────────────────────── */}
            {ROUTES.map((r) => {
              const stroke =
                r.color === 'mint'
                  ? `url(#${trunkGrad})`
                  : r.color === 'violet-soft'
                    ? '#B0ABF2'
                    : '#6558DF'
              return (
                <g key={r.id}>
                  {/* Ghost line — whole route shown very faintly so composition reads before it draws */}
                  <path
                    d={r.d}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={r.weight * 0.5}
                    strokeLinecap="round"
                    opacity="0.14"
                  />
                  {/* Draw-on line */}
                  <path
                    d={r.d}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={r.weight}
                    strokeLinecap="round"
                    pathLength="100"
                    strokeDasharray="100"
                    strokeDashoffset="100"
                    opacity="0.85"
                    style={{
                      animation: `cinqo-draw 4.8s ${r.delay}s cubic-bezier(.65,.05,.36,1) infinite`,
                    }}
                  />
                  {/* Flowing particle */}
                  <circle
                    r={r.color === 'mint' ? 2.1 : 1.6}
                    fill={r.color === 'mint' ? '#20B088' : '#3D2ECC'}
                  >
                    <animateMotion
                      dur="4.8s"
                      begin={`${r.delay + 0.3}s`}
                      repeatCount="indefinite"
                      keyPoints="0;1"
                      keyTimes="0;1"
                      path={r.d}
                    />
                    <animate
                      attributeName="opacity"
                      values="0;1;1;0"
                      keyTimes="0;0.1;0.9;1"
                      dur="4.8s"
                      begin={`${r.delay + 0.3}s`}
                      repeatCount="indefinite"
                    />
                  </circle>
                </g>
              )
            })}

            {/* ── Collection points (small pulsing dots) ─────────── */}
            {[...pointsA, ...pointsB].map((p, i) => (
              <g key={`pt-${i}`}>
                <circle cx={p.x} cy={p.y} r="1.8" fill="#FFFFFF" stroke="#3D2ECC" strokeWidth="1" />
                <circle
                  cx={p.x}
                  cy={p.y}
                  r="1.8"
                  fill="none"
                  stroke="#3D2ECC"
                  strokeWidth="0.8"
                  opacity="0.6"
                  style={{
                    animation: `cinqo-pulse-dot 2.8s ${(i * 0.22) % 2.8}s ease-out infinite`,
                    transformOrigin: `${p.x}px ${p.y}px`,
                  }}
                />
              </g>
            ))}

            {/* ── Hubs (H1, H2) — pulsing rings + filled center ─── */}
            {[H1, H2].map((h, i) => (
              <g key={`hub-${i}`}>
                <circle
                  cx={h.x}
                  cy={h.y}
                  r="5"
                  fill="none"
                  stroke="#3D2ECC"
                  strokeWidth="1"
                  opacity="0.35"
                  style={{
                    animation: `cinqo-ripple 2.4s ${i * 0.8}s ease-out infinite`,
                    transformOrigin: `${h.x}px ${h.y}px`,
                  }}
                />
                <circle
                  cx={h.x}
                  cy={h.y}
                  r="5"
                  fill="none"
                  stroke="#3D2ECC"
                  strokeWidth="1"
                  opacity="0.35"
                  style={{
                    animation: `cinqo-ripple 2.4s ${i * 0.8 + 1.0}s ease-out infinite`,
                    transformOrigin: `${h.x}px ${h.y}px`,
                  }}
                />
                <circle cx={h.x} cy={h.y} r="3.6" fill="#FFFFFF" stroke="#3D2ECC" strokeWidth="1.3" />
                <circle cx={h.x} cy={h.y} r="1.6" fill="#3D2ECC" />
              </g>
            ))}

            {/* ── Facility (destination) — mint, square-ish to read as building ── */}
            <g>
              <rect
                x={F.x - 5.5}
                y={F.y - 5.5}
                width="11"
                height="11"
                rx="1.5"
                fill="none"
                stroke="#20B088"
                strokeWidth="1"
                opacity="0.35"
                style={{
                  animation: 'cinqo-ripple-sq 3s 0.4s ease-out infinite',
                  transformOrigin: `${F.x}px ${F.y}px`,
                }}
              />
              <rect
                x={F.x - 4}
                y={F.y - 4}
                width="8"
                height="8"
                rx="1.2"
                fill="#FFFFFF"
                stroke="#20B088"
                strokeWidth="1.4"
              />
              <rect
                x={F.x - 1.8}
                y={F.y - 1.8}
                width="3.6"
                height="3.6"
                rx="0.6"
                fill="#20B088"
              />
            </g>
          </g>

          {/* Outer hairline border — technical/dashboard feel */}
          <rect
            x="0.5"
            y="0.5"
            width="199"
            height="199"
            rx="15.5"
            ry="15.5"
            fill="none"
            stroke="#E8E9F0"
            strokeWidth="1"
          />
        </svg>

        <style>{`
          @keyframes cinqo-draw {
            0%   { stroke-dashoffset: 100; opacity: 0; }
            6%   { opacity: 0.9; }
            32%  { stroke-dashoffset: 0; opacity: 0.9; }
            80%  { stroke-dashoffset: 0; opacity: 0.9; }
            100% { stroke-dashoffset: 0; opacity: 0.9; }
          }
          @keyframes cinqo-pulse-dot {
            0%   { transform: scale(1);   opacity: 0.9; }
            50%  { transform: scale(2.4); opacity: 0;   }
            100% { transform: scale(2.4); opacity: 0;   }
          }
          @keyframes cinqo-ripple {
            0%   { transform: scale(0.6); opacity: 0.6; }
            100% { transform: scale(2.6); opacity: 0;   }
          }
          @keyframes cinqo-ripple-sq {
            0%   { transform: scale(0.7); opacity: 0.6; }
            100% { transform: scale(2.2); opacity: 0;   }
          }
          @media (prefers-reduced-motion: reduce) {
            .cinqo-loader * {
              animation-duration: 0.01ms !important;
              animation-iteration-count: 1 !important;
            }
            .cinqo-loader path[stroke-dasharray="100"] {
              stroke-dashoffset: 0 !important;
              opacity: 0.9 !important;
            }
          }
        `}</style>
      </div>
    )
  },
)

CinqoLoader.displayName = 'CinqoLoader'
